/**
 * Cancelled Transactions Table Component
 * Displays cancelled/failed payment gateway transactions
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Badge } from 'primereact/badge';
import LoadingComponent from '../../loadingComponent';

export class CancelledTransactionsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            isLoading: false
        };
        
        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();
        
        this.snoBodyTemplate = this.snoBodyTemplate.bind(this);
        this.orderIdBodyTemplate = this.orderIdBodyTemplate.bind(this);
        this.receiptDateBodyTemplate = this.receiptDateBodyTemplate.bind(this);
        this.paymentModeBodyTemplate = this.paymentModeBodyTemplate.bind(this);
        this.paidAmountBodyTemplate = this.paidAmountBodyTemplate.bind(this);
        this.totalBodyTemplate = this.totalBodyTemplate.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
        this.syncScroll = this.syncScroll.bind(this);
    }

    async componentDidMount() {
        const { cancelledTransactionsData, cancelledTransactionsLoading } = this.props;

        if (cancelledTransactionsData) {
            this.processTransactionsData(cancelledTransactionsData);
        } else if (cancelledTransactionsLoading === false) {
            this.setState({ isLoading: false, transactions: [] });
        }

        // Set up scroll synchronization
        setTimeout(() => {
            this.attachScrollListener();
        }, 100);
    }

    attachScrollListener = () => {
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.removeEventListener('scroll', this.syncScroll);
            dataWrapper.addEventListener('scroll', this.syncScroll);
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const grandTotalWrapper = this.grandTotalScrollRef.current;
        if (grandTotalWrapper) {
            const grandTotalInner = grandTotalWrapper.querySelector('div');
            if (grandTotalInner) {
                grandTotalInner.style.transform = `translateX(-${scrollLeft}px)`;
            }
        }
    }

    processTransactionsData = (data) => {
        const transactions = data?.collections;

        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            this.setState({
                transactions: transactions,
                isLoading: false
            }, () => {
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            this.setState({
                transactions: [],
                isLoading: false
            });
        }
    }

    async componentDidUpdate(prevProps) {
        const { cancelledTransactionsData, cancelledTransactionsLoading } = this.props;
        
        if (cancelledTransactionsData !== prevProps.cancelledTransactionsData && cancelledTransactionsData) {
            this.processTransactionsData(cancelledTransactionsData);
        } else if (cancelledTransactionsLoading === false && prevProps.cancelledTransactionsLoading === true && !cancelledTransactionsData) {
            this.setState({ isLoading: false, transactions: [] });
        }
    }

    componentWillUnmount() {
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    formatDateTime = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    calculateGrandTotal = () => {
        const { transactions } = this.state;
        return transactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
    };

    snoBodyTemplate(rowData, options) {
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {options.rowIndex + 1}
            </span>
        );
    }

    receiptDateBodyTemplate(rowData) {
        return <span>{this.formatDate(rowData?.transactionDate || rowData.receiptDate)}</span>;
    }

    orderIdBodyTemplate(rowData) {
        return (
            <div className="transaction-info">
                <div className="receipt-number">{rowData.orderId}</div>
            </div>
        );
    }

    paymentModeBodyTemplate(rowData) {
        const getStatusSeverity = (status) => {
            switch (status) {
                case 'CANCELLED':
                    return 'danger';
                case 'EXPIRED':
                    return 'warning';
                case 'FAILED':
                    return 'danger';
                case 'USER_DROPPED':
                    return 'warning';
                default:
                    return 'danger';
            }
        };

        return (
            <div className="payment-info">
                <div className="payment-mode">{rowData.paymentMode}</div>
                <div className="payment-gateway" style={{ fontSize: '0.85rem', color: '#666' }}>
                    Gateway: {rowData.paymentGateway}
                </div>
            </div>
        );
    }

    paidAmountBodyTemplate(rowData) {
        return <span>₹{rowData.amount?.toFixed(2)}</span>;
    }

    totalBodyTemplate(rowData) {
        return <span>₹{rowData.amount?.toFixed(2)}</span>;
    }

    render() {
        const { transactions, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();

        return (
            <>
                <div className="transactions-table-container">
                    <div className="info-note">
                        <i className="pi pi-info-circle"></i>
                        <span>Cancelled/Failed payment gateway transactions are displayed below.</span>
                    </div>

                    {isLoading && <LoadingComponent />}
                    
                    {!isLoading && (
                        <div className="transactions-table-wrapper">
                            <div ref={this.dataScrollRef}>
                                <DataTable
                                    value={transactions}
                                    scrollable 
                                    scrollHeight="flex"
                                    className="transactions-table"
                                    showGridlines
                                    emptyMessage="No cancelled transactions found"
                                >
                                    <Column 
                                        header="S.No" 
                                        body={this.snoBodyTemplate} 
                                        headerClassName="sno-header"
                                        bodyClassName="sno-cell"
                                        style={{ width: '80px', textAlign: 'center' }}
                                    />
                                    <Column 
                                        header="Date" 
                                        body={this.receiptDateBodyTemplate} 
                                        headerClassName="receipt-date-header"
                                        bodyClassName="receipt-date-cell"
                                        style={{ width: '120px' }}
                                    />
                                    <Column 
                                        header="Order ID" 
                                        body={this.orderIdBodyTemplate} 
                                        headerClassName="transaction-id-header"
                                        bodyClassName="transaction-id-cell"
                                        style={{ width: '350px' }}
                                    />
                                    <Column 
                                        header="Payment Mode" 
                                        body={this.paymentModeBodyTemplate} 
                                        headerClassName="payment-mode-header"
                                        bodyClassName="payment-mode-cell"
                                        style={{ width: '280px' }}
                                    />
                                    <Column 
                                        header="Amount" 
                                        body={this.paidAmountBodyTemplate} 
                                        headerClassName="paid-amount-header"
                                        bodyClassName="paid-amount-cell"
                                        style={{ width: '150px', textAlign: 'right' }}
                                    />
                                    <Column 
                                        header="Total" 
                                        body={this.totalBodyTemplate} 
                                        headerClassName="total-header"
                                        bodyClassName="total-cell"
                                        style={{ width: '150px', textAlign: 'right' }}
                                    />
                                </DataTable>
                            </div>

                            {transactions.length > 0 && (
                                <div ref={this.grandTotalScrollRef} style={{ overflow: 'hidden' }}>
                                    <div style={{ width: 'max-content' }}>
                                        <DataTable
                                            value={[{ total: grandTotal }]}
                                            className="transactions-grand-total-table cancelled-grand-total"
                                            showGridlines
                                        >
                                            <Column header="" body={() => ''} style={{ width: '80px', minWidth: '80px' }} />
                                            <Column header="" body={() => ''} style={{ width: '120px', minWidth: '120px' }} />
                                            <Column header="" body={() => ''} style={{ width: '350px', minWidth: '350px' }} />
                                            <Column 
                                                header="" 
                                                body={() => <strong style={{ color: '#076EFF', display: 'block', textAlign: 'right' }}>GRAND TOTAL :</strong>} 
                                                style={{ width: '280px', minWidth: '280px' }}
                                            />
                                            <Column 
                                                header="" 
                                                body={() => <strong style={{ color: '#076EFF', display: 'block', textAlign: 'right' }}>₹{grandTotal?.toFixed(2)}</strong>} 
                                                style={{ width: '150px', minWidth: '150px' }}
                                            />
                                            <Column header="" body={() => ''} style={{ width: '150px', minWidth: '150px' }} />
                                        </DataTable>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </>
        );
    }
}

export default CancelledTransactionsTable;
